import React, { useCallback, useEffect, useState } from 'react';
import { roleLabel } from './roleLabel';

/**
 * AccessSection — the access (protect/unprotect) control for a node or station,
 * extracted from the former standalone ProtectModal (admin-node-station-edit
 * slug) so it can live as a SECTION inside the Node Edit + Station Edit dialogs
 * instead of its own modal. Pure inner content (no overlay/footer) — the host
 * SectionedDialog owns the chrome.
 *
 * Same BFF contract as before: GET `/api/user-access/resources/{level}/{id}`
 * for state; POST/DELETE `…/protect` to protect/unprotect. Protecting an asset
 * mints a role granting it (the shadow rule then negates default access).
 */
export default function AccessSection({ level, id, name, onChanged }) {
  const [state, setState] = useState(null);   // {protected, granting_roles}
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/api/user-access/resources/${level}/${id}`;

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await fetch(base, { credentials: 'same-origin' });
      if (!r.ok) { setError(`HTTP ${r.status}: ${await r.text()}`); return; }
      setState(await r.json());
    } catch (e) { setError(String(e.message || e)); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const act = async (method, what = 'protect') => {
    setBusy(true);
    const r = await fetch(`${base}/${what}`, { method, credentials: 'same-origin' });
    setBusy(false);
    if (!r.ok) { window.alert(`${method} → HTTP ${r.status}\n${await r.text()}`); return; }
    await load();
    if (onChanged) onChanged();   // let the host refresh shield badges
  };

  const protectedNow = state && state.protected;
  const openNow = !!(state && state.open_to_everyone);
  return (
    <div>
      <p style={{ fontSize: '0.82rem', color: '#555', marginTop: 0 }}>
        Protecting this {level} mints a role granting access to it. By the shadow rule, default
        (public / other-role) access is then negated — only roles granted this asset reach it.
        Assign the protect role to users in the <strong>Users</strong> tab.
      </p>
      {error && <div style={{ color: '#c00', fontSize: '0.82rem' }}>{error}</div>}
      {!state && !error && <div style={{ color: '#888' }}>Loading…</div>}
      {state && (
        <>
          {/* Open to everyone supersedes protection, so it comes first and disables the switch below. */}
          {level === 'station' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0' }}>
              <button type="button" role="switch" aria-checked={openNow} disabled={busy}
                      aria-label={`Open to everyone ${openNow ? 'on' : 'off'}`}
                      onClick={() => act(openNow ? 'DELETE' : 'POST', 'open')}
                      style={{ ...switchTrack, background: openNow ? '#5fa67a' : '#bbb',
                               cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
                <span style={{ ...switchThumb, transform: openNow ? 'translateX(20px)' : 'translateX(0)' }} />
              </button>
              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Open to everyone</span>
              <span style={{ fontSize: '0.78rem', color: '#888' }}>
                {openNow ? '— anyone can open it, signed in or not, even on a protected network'
                  : '— off: its protection, node and network decide'}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '12px 0',
                        opacity: openNow ? 0.5 : 1 }}>
            <button type="button" role="switch" aria-checked={protectedNow && !openNow}
                    disabled={busy || openNow}
                    aria-label={openNow ? 'Protection superseded' : `Protection ${protectedNow ? 'on' : 'off'}`}
                    onClick={() => act(protectedNow ? 'DELETE' : 'POST')}
                    style={{ ...switchTrack, background: protectedNow && !openNow ? '#5fa67a' : '#bbb',
                             cursor: busy || openNow ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              <span style={{ ...switchThumb,
                             transform: protectedNow && !openNow ? 'translateX(20px)' : 'translateX(0)' }} />
            </button>
            <span style={{ fontSize: '0.85rem', fontWeight: 600,
                           color: openNow ? '#888' : protectedNow ? '#a33' : '#363' }}>
              {openNow ? 'Superseded by Open to everyone' : protectedNow ? 'Protected' : 'Open'}
            </span>
            {!openNow && (
              <span style={{ fontSize: '0.78rem', color: '#888' }}>
                {protectedNow ? '— restricted to granted roles' : '— its node and network decide'}
              </span>
            )}
          </div>
          {protectedNow && state.granting_roles && state.granting_roles.length > 0 && (
            <div style={{ fontSize: '0.8rem', marginBottom: 10, opacity: openNow ? 0.5 : 1 }}>
              <div style={{ color: '#666', marginBottom: 4 }}>
                {openNow ? 'Roles granted (no effect while open to everyone):' : 'Roles that can reach it:'}
              </div>
              {state.granting_roles.map((r) => (
                <span key={r.id} style={{
                  background: '#eef', border: '1px solid #dde', borderRadius: 3,
                  padding: '1px 6px', fontSize: '0.75rem', marginRight: 4,
                  opacity: r.revoked ? 0.45 : 1,
                }}>{roleLabel(r.name)}{r.revoked ? ' (revoked)' : ''}</span>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Two-position On/Off switch (admin-node-station-edit): On = protected, Off =
// open. Replaces the former protect/unprotect buttons.
const switchTrack = {
  position: 'relative', width: 44, height: 24, borderRadius: 12, border: 'none',
  padding: 0, transition: 'background 0.15s', flexShrink: 0,
};
const switchThumb = {
  position: 'absolute', top: 2, left: 2, width: 20, height: 20, borderRadius: '50%',
  background: '#fff', transition: 'transform 0.15s', boxShadow: '0 1px 2px rgba(0,0,0,0.3)',
};
